import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { pollService } from '../services/pollService';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBarChart3, FiCalendar, FiClock, FiUser, FiMail, FiCheck } = FiIcons;

const PollPage = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  const { register, handleSubmit, formState: { errors } } = useForm();

  const requireName = poll?.settings?.requireName;
  const requireEmail = poll?.settings?.requireEmail;

  useEffect(() => {
    loadPoll();
  }, [shareId]);

  const loadPoll = async () => {
    try {
      setLoading(true);
      const pollData = await pollService.getPollByShareId(shareId);
      setPoll(pollData);
      
      // Check if poll has deadline and is expired
      if (pollData.settings.deadline && new Date() > new Date(pollData.settings.deadline)) {
        toast.error('Diese Umfrage ist abgelaufen');
      }
    } catch (error) {
      toast.error('Umfrage nicht gefunden');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    if (!selectedOption) {
      toast.error('Bitte wählen Sie eine Option aus');
      return;
    }

    setSubmitting(true);
    try {
      await pollService.submitVote(shareId, selectedOption.id, {
        name: data.name || 'Anonym',
        email: data.email || ''
      });

      toast.success('Ihre Stimme wurde erfolgreich abgegeben!');
      navigate(`/poll/${shareId}/results`);
    } catch (error) {
      toast.error(error.message || 'Fehler beim Abgeben der Stimme');
    } finally {
      setSubmitting(false);
    }
  };

  const isExpired = poll?.settings?.deadline && new Date() > new Date(poll.settings.deadline);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!poll) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Umfrage nicht gefunden
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Die angeforderte Umfrage existiert nicht oder ist nicht verfügbar.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon icon={FiBarChart3} className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {poll.title}
          </h1>
          {poll.description && (
            <p className="text-lg text-gray-600 dark:text-gray-400">
              {poll.description}
            </p>
          )}
          {poll.settings.deadline && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Abstimmung bis: {format(new Date(poll.settings.deadline), 'dd.MM.yyyy HH:mm', { locale: de })}
            </p>
          )}
        </motion.div>

        {isExpired ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="p-8 text-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Abstimmung beendet
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Die Deadline für diese Umfrage ist abgelaufen.
              </p>
              <Button onClick={() => navigate(`/poll/${shareId}/results`)}>
                Ergebnisse anzeigen
              </Button>
            </Card>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Voter Information */}
            {(requireName || requireEmail) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Card className="p-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Ihre Daten
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {requireName && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Name *
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <SafeIcon icon={FiUser} className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                              errors.name ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                            } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                            placeholder="Ihr Name"
                            {...register('name', { required: requireName ? 'Name ist erforderlich' : false })}
                          />
                        </div>
                        {errors.name && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                            {errors.name.message}
                          </p>
                        )}
                      </div>
                    )}

                    {requireEmail && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          E-Mail *
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <SafeIcon icon={FiMail} className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="email"
                            className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                              errors.email ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                            } bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                            placeholder="ihre@email.de"
                            {...register('email', {
                              required: requireEmail ? 'E-Mail ist erforderlich' : false,
                              pattern: requireEmail ? {
                                value: /^\S+@\S+$/i,
                                message: 'Ungültige E-Mail-Adresse'
                              } : undefined
                            })}
                          />
                        </div>
                        {errors.email && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                            {errors.email.message}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              </motion.div>
            )}

            {/* Poll Options */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Terminvorschläge
                  <span className="text-sm font-normal text-gray-600 dark:text-gray-400 ml-2">
                    (Bitte wählen Sie einen Termin aus)
                  </span>
                </h2>
                <div className="space-y-4">
                  {poll.options.map((option, index) => (
                    <motion.div
                      key={option.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 * index }}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedOption(option)}
                        className={`w-full p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                          selectedOption?.id === option.id
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                            : 'border-gray-200 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-600 bg-gray-50 dark:bg-gray-700'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center text-lg font-medium text-gray-900 dark:text-white mb-1">
                              <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-2" />
                              {format(parseISO(option.date), 'EEEE, dd. MMMM yyyy', { locale: de })}
                            </div>
                            <div className="flex items-center text-gray-600 dark:text-gray-400">
                              <SafeIcon icon={FiClock} className="w-4 h-4 mr-2" />
                              {option.time} ({option.duration} Min.)
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                              {option.votes.length} Stimme{option.votes.length !== 1 ? 'n' : ''}
                            </div>
                          </div>
                          {selectedOption?.id === option.id && (
                            <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center">
                              <SafeIcon icon={FiCheck} className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                      </button>
                    </motion.div>
                  ))}
                </div>
              </Card>
            </motion.div>

            {/* Submit */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex justify-center"
            >
              <Button type="submit" loading={submitting} size="lg">
                Stimme abgeben
              </Button>
            </motion.div>
          </form>
        )}
      </div>
    </div>
  );
};

export default PollPage;